import { useEffect, useState, type ReactNode } from "react";
import { useHudStore } from "../store/hudStore";
import { peso } from "../data/seed";
import { Badge, Button, Card, Field, Progress, Select } from "../design-system/components";
import "./financeDetail.css";
import { refreshFinances } from "../lib/live";
import { submitDebtPayment } from "../lib/loans";
import { currentAuthGeneration, requireCurrentAuth } from "../lib/auth";
import { useStaggeredGrid } from "../lib/useStaggeredGrid";
import Page from "../components/Page";
import type { LoanData } from "../types";
import { isReceivableDebt, receivableBorrower } from "../lib/financeAnalytics";
import { pendingCollection } from "../lib/collectionRequests";

// Full loan/debt detail page (screen: "debt"). Progress, terms, the share panel,
// this loan's payment history, and (for shared loans) the complete schedule,
// fetched from the same /loan/<slug>.json that drives the public page (one
// source, no DynamoDB duplication). Reached from a debt row in the Finances
// module (FinanceModule → st.openDebt).

// The schedule/balances carry centavos, so format with 2 decimals here (the
// HUD-wide `peso` is whole-peso, used for the summary numbers to match the module).
const peso2 = (n: number): string => "₱" + n.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const todayStr = () => { const t = new Date(Date.now() + 8 * 3600 * 1000); return `${MON[t.getUTCMonth()]} ${t.getUTCDate()}, ${t.getUTCFullYear()}`; };

function DebtWorkspace({ children }: { children: ReactNode }) {
  const workspaceRef = useStaggeredGrid();
  return <div ref={workspaceRef} className="finance-debt-workspace">{children}</div>;
}

export default function Debt() {
  const st = useHudStore();
  const d = st.debts.find((x) => x.id === st.debtId);
  const [loan, setLoan] = useState<LoanData | null>(null);
  const [schedErr, setSchedErr] = useState(false);
  const [copied, setCopied] = useState(false);
  // Log-payment form state.
  const [logOpen, setLogOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [payDate, setPayDate] = useState("");
  // Optional cash account: payables debit it and receivables credit it.
  const [payAccount, setPayAccount] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const slug = d?.slug;
  const isPublic = !!d?.public;

  // Fetch the schedule for shared loans from the public JSON on this origin.
  // In dev (vite) that path 404s. We just fall back to the summary + payments.
  useEffect(() => {
    if (!isPublic || !slug) { setLoan(null); setSchedErr(false); return; }
    const ac = new AbortController();
    setSchedErr(false);
    fetch(`/loan/${encodeURIComponent(slug)}.json`, { signal: ac.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("http " + r.status))))
      .then((data: LoanData) => setLoan(data))
      .catch((e) => { if ((e as Error).name !== "AbortError") { setLoan(null); setSchedErr(true); } });
    return () => ac.abort();
  }, [slug, isPublic]);

  if (!d) {
    return (
      <Page back="Back to Finances" onBack={st.closeDebt}>
        <Card><h1 className="detail-heading detail-title">That loan is gone.</h1></Card>
      </Page>
    );
  }

  // A loan's truth: `start` is the historical total and paid = start - balance.
  // A revolving card has no start, so that collapses to paid = 0 even with
  // payments on record. For a start-less debt the payment log IS the history:
  // paid = what the log sums to, and the "of" total is what was owed before
  // those payments (balance + paid), so the three figures stay consistent.
  const loggedPaid = (d.payments ?? []).reduce((t, p) => t + (Number(p.amount) || 0), 0);
  const paid = d.start ? Math.max(0, d.start - d.balance) : loggedPaid;
  const total = d.start ?? d.balance + loggedPaid;
  const pct = total ? Math.round((paid / total) * 100) : 0;
  const apr = d.apr ?? 0;
  const receivable = isReceivableDebt(d);
  const borrower = receivable ? receivableBorrower(d.name) : "";

  // Live rows from the API (reflect a just-logged payment) win over the static JSON.
  const rows = d.rows && d.rows.length ? d.rows : loan?.rows ?? [];
  const made = rows.filter((r) => r.status === "paid").length;
  const firstUpcoming = rows.findIndex((r) => r.status !== "paid");
  const payoff = rows.length ? rows[rows.length - 1]?.due : undefined;
  const monthly = d.plan_monthly ?? loan?.monthly ?? d.monthly;
  const paidRows = rows.filter((r) => r.status === "paid");
  const nextDue = firstUpcoming >= 0 ? rows[firstUpcoming]?.due : d.next;
  const paymentDetails = loan?.paymentDetails;

  const openLog = () => {
    const pending = pendingCollection(d.id);
    setAmount(pending ? String(pending.amount) : monthly ? String(monthly) : "");
    setPayDate(pending?.date || todayStr()); setPayAccount(pending?.account || ""); setFile(null); setErr(""); setLogOpen(true);
  };
  const submitLog = async () => {
    const generation = currentAuthGeneration();
    const amt = Number(amount);
    if (!amt) { setErr("Enter an amount."); return; }
    setBusy(true); setErr("");
    try {
      const date = payDate.trim() || todayStr();
      const account = payAccount || undefined;
      await submitDebtPayment({ debtId: d.id, amount: amt, date, file: file ?? undefined, account }, generation);
      requireCurrentAuth(generation);
      setLogOpen(false); setAmount(""); setFile(null);
      await refreshFinances();
    } catch (e) {
      if (generation === currentAuthGeneration()) setErr((e as Error).message || (receivable ? "Couldn't record the collection." : "Couldn't log the payment."));
    }
    finally { if (generation === currentAuthGeneration()) setBusy(false); }
  };

  // Prefer the full share URL stored on the debt; fall back to an origin-relative
  // link (correct in prod, where the page is served from the same host).
  const shareUrl = d.share_url || (slug ? `${location.origin}/loan/${slug}` : "");

  const copy = () => {
    if (!shareUrl) return;
    navigator.clipboard?.writeText(shareUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }).catch(() => {});
  };

  const stat = (label: string, value: string, accent?: string) => (
    <div className="finance-detail-metric"><span>{label}</span><strong style={{ color: accent || "var(--ink)" }}>{value}</strong></div>
  );

  return (
    <Page back="Back to Finances" onBack={st.closeDebt}>
      <Card className="detail-stack">
        <div className="finance-detail-badges">
          {/* Monthly, matching the finance-module row and the quoted charge. */}
          <Badge tone={apr > 0 ? "warning" : "success"}><b className="detail-status-dot" style={{ background: "currentColor" }} />{apr ? +(apr / 12).toFixed(2) + "% / mo" : "Interest-free"}</Badge>
          {receivable ? <Badge tone="accent">{borrower ? `from ${borrower}` : "Owed to you"}</Badge> : d.lender && <Badge>to {d.lender}</Badge>}
          <Badge tone={isPublic ? "accent" : "neutral"}><i aria-hidden="true" className={isPublic ? "fa-solid fa-link" : "fa-solid fa-lock"} />{isPublic ? "Shared" : "Private"}</Badge>
        </div>
        <h1 className="detail-heading detail-title">{d.name}</h1>
        <div className="finance-paid-summary">
          <div className="finance-detail-total"><span>{receivable ? "Received so far" : "Paid so far"}</span><strong>{peso(paid)} <small>of {peso(total)}</small></strong></div>
          <div className="finance-still-owing"><span>{receivable ? "Still to collect" : "Still owing"}</span><strong>{peso(d.balance)}</strong></div>
        </div>
        <Progress value={pct} label={receivable ? "Amount collected" : "Debt paid"} valueLabel={`${pct}% ${receivable ? "collected" : "cleared"}`} />
        {rows.length > 0 && <p className="finance-detail-note">{made} of {rows.length} {receivable ? "instalments collected in full" : "payments made"}</p>}
      </Card>

      <Card>
        <div className="finance-detail-metrics">
          {stat("Total", peso(total))}
          {monthly ? stat("Monthly", peso2(monthly)) : null}
          {stat(receivable ? "Next collection" : "Next due", nextDue || "None", "var(--orange-ink)")}
          {payoff ? stat(receivable ? "Final collection" : "Projected payoff", payoff) : null}
          {/* Facility headroom stays shared across sibling loans, including negative values. */}
          {(() => {
            if (!d.limit) return null;
            const drawn = d.drawn ?? d.balance;
            const avail = d.limit - drawn;
            const label = d.facility ? "Left on the line" : "Credit left";
            return stat(
              label,
              (avail < 0 ? "−" : "") + peso(Math.abs(avail)),
              avail < 0 ? "var(--danger-ink)" : avail / d.limit <= 0.2 ? "var(--orange)" : "var(--ink)",
            );
          })()}
          {!!d.limit && stat("Credit limit", peso(d.limit))}
          {receivable ? borrower && stat("Borrower", borrower) : d.lender ? stat("Lender", d.lender) : null}
        </div>
      </Card>

      <DebtWorkspace>
      {!receivable && paymentDetails && <Card className="detail-stack finance-payment-card" data-hud-lane="auto" title="Payment details">
        <div className="finance-payment-details">
          <div className="finance-payment-copy">
            <dl className="finance-payment-recipient">
              <div><dt>Bank</dt><dd>{paymentDetails.bank}</dd></div>
              <div><dt>Recipient</dt><dd>{paymentDetails.recipient}</dd></div>
              {paymentDetails.accountEnding && <div><dt>Account</dt><dd>Ending in {paymentDetails.accountEnding}</dd></div>}
            </dl>
            <p className="finance-payment-instructions">Scan this QR code in your banking app to send a payment to {d.lender || paymentDetails.recipient}.</p>
            <a className="ds-button ds-button--secondary ds-button--compact ds-focusable finance-qr-open" href={paymentDetails.qrImage} target="_blank" rel="noopener noreferrer"><i aria-hidden="true" className="fa-solid fa-arrow-up-right-from-square" />Open QR image</a>
          </div>
          <img className="finance-payment-qr" src={paymentDetails.qrImage} width="846" height="942" loading="lazy" alt={`${paymentDetails.recipient}'s ${paymentDetails.bank} InstaPay payment QR code`} />
        </div>
      </Card>}

      <Card className="detail-stack" data-hud-lane="auto" title="Shareable page" actions={<Badge tone={isPublic ? "accent" : "neutral"}>{isPublic ? "Public" : "Private"}</Badge>}>
        {isPublic && shareUrl ? (
          <>
            <div className="finance-share-row">
              <span className="finance-share-url">{shareUrl}</span>
              <Button size="compact" variant="primary" onClick={copy} icon={copied ? "fa-solid fa-check" : "fa-solid fa-copy"}>{copied ? "Copied" : "Copy"}</Button>
              <a className="ds-button ds-button--secondary ds-button--compact ds-focusable" href={shareUrl} target="_blank" rel="noopener noreferrer"><i aria-hidden="true" className="fa-solid fa-arrow-up-right-from-square" />Open</a>
              <span className="ds-sr-only" role="status">{copied ? "Link copied." : ""}</span>
            </div>
            <p className="finance-detail-note">Anyone with this link can view the schedule and progress, nothing else about your finances.</p>
          </>
        ) : <p className="finance-detail-note">This {receivable ? "collection record" : "loan"} is private.</p>}
      </Card>

      <Card className="detail-stack" data-hud-lane="auto" title={receivable ? "Collection history" : "Payment history"} actions={<>{paid > 0 && <Badge tone="success">{peso(paid)} {receivable ? "received" : "paid"}</Badge>}<Button size="compact" variant={logOpen ? "secondary" : "primary"} onClick={() => logOpen ? setLogOpen(false) : openLog()} icon={logOpen ? "fa-solid fa-xmark" : "fa-solid fa-plus"}>{logOpen ? "Cancel" : receivable ? "Record collection" : "Log payment"}</Button></>}>
        {logOpen && <div className="finance-payment-form">
          <div className="finance-payment-fields">
            <Field id="debt-payment-amount" label="Amount (₱)" value={amount} onChange={(event) => setAmount(event.target.value.replace(/[^0-9.]/g, ""))} inputMode="decimal" placeholder={monthly ? String(monthly) : "0.00"} error={err === "Enter an amount." ? err : undefined} />
            <Field id="debt-payment-date" label={receivable ? "Date received" : "Date paid"} value={payDate} onChange={(event) => setPayDate(event.target.value)} placeholder={todayStr()} />
          </div>
          {st.accounts.length > 0 && <Select id="debt-payment-account" label={receivable ? "Received into (optional)" : "Paid from (optional)"} value={payAccount} onChange={(event) => setPayAccount(event.target.value)}><option value="">{receivable ? "No destination account" : "No source account"}</option>{st.accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}</Select>}
          <Field id="debt-payment-receipt" className="finance-file-field" label="Receipt image (optional)" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setFile(event.target.files?.[0] ?? null)} helper={file?.name} />
          <p className="finance-detail-note">{receivable ? 'Record money after you receive it. Pick a "Received into" account to add the collection to that balance. Without an account, the collection is recorded as income and the balance owed decreases, but no bank balance changes.' : 'Leave the standard amount, or type a custom one. The schedule re-flows. Pick a "Paid from" account to also subtract the payment from that balance. The public page updates on its next load.'}</p>
          {err && err !== "Enter an amount." && <p className="finance-payment-error" role="alert">{err}</p>}
          <div className="finance-payment-actions"><Button size="compact" disabled={busy} onClick={() => setLogOpen(false)}>Cancel</Button><Button size="compact" variant="primary" loading={busy} onClick={submitLog}>{busy ? "Saving…" : receivable ? "Record collection" : "Log payment"}</Button></div>
        </div>}
        <div>{paidRows.length > 0 && (!receivable || !d.payments) ? paidRows.map((r, i) => (
          <div key={r.n} className="finance-payment-row" style={{ borderTop: i ? "1px solid var(--line)" : "none" }}>
            <span className="finance-detail-date">{r.paidDate || r.due}</span><strong className="finance-payment-label">{receivable ? "Collection" : "Payment"} #{r.n}</strong>
            {r.receipt && <a className="finance-receipt-link" href={r.receipt} target="_blank" rel="noopener noreferrer"><i aria-hidden="true" className="fa-solid fa-link" />Receipt</a>}
            <span className="finance-detail-money finance-payment-amount">{peso2(r.amount)}</span>
          </div>
        )) : (d.payments && d.payments.length > 0) ? (
          // Collections use the actual receipt log so partial and early amounts
          // retain their date, destination account, and receipt. Cards do too.
          [...d.payments].reverse().map((p, i) => {
            const acct = st.accounts.find((a) => a.id === p.account);
            return <div key={`${p.date}-${i}`} className="finance-payment-row" style={{ borderTop: i ? "1px solid var(--line)" : "none" }}>
              <span className="finance-detail-date">{p.date}</span><strong className="finance-payment-label">{receivable ? "Collection" : "Payment"} #{d.payments!.length - i}</strong>
              {acct && <Badge>{receivable ? "into" : "from"} {acct.name}</Badge>}
              {p.receipt && <a className="finance-receipt-link" href={p.receipt} target="_blank" rel="noopener noreferrer"><i aria-hidden="true" className="fa-solid fa-link" />Receipt</a>}
              <span className="finance-detail-money finance-payment-amount">{peso2(p.amount)}</span>
            </div>;
          })
        ) : <p className="detail-copy">{receivable ? "No collections received yet. Record the first when it arrives." : "No payments recorded yet. Log your first above."}</p>}</div>
      </Card>

      <Card data-hud-lane="auto" title={receivable ? "Collection schedule" : "Payment schedule"} actions={rows.length > 0 ? <Badge>{rows.length} {receivable ? "instalments" : "payments"}</Badge> : undefined}>
        {rows.length > 0 ? <div>{rows.map((r, j) => {
          const isNext = j === firstUpcoming;
          // Keep separators away from the highlighted next payment's corners.
          const sep = j !== 0 && j !== firstUpcoming && j !== firstUpcoming + 1;
          return <div key={r.n} className="debt-schedule-row" style={{ ...(isNext ? { background: "linear-gradient(90deg, rgba(110,168,255,.16), rgba(110,168,255,.03))", border: "1px solid var(--line2)", borderRadius: 8 } : { borderTop: sep ? "1px solid var(--line)" : "none" }) }}>
            <span className="finance-detail-date">{r.n}</span>
            <span className="debt-schedule-date"><span className="finance-schedule-due">{r.due}</span>
              {r.status === "paid" && r.paidDate && <span className="finance-schedule-paid-date">✓ {receivable ? "received" : "paid"} {r.paidDate}</span>}
              {r.status === "paid" && r.receipt && <a className="finance-receipt-link" href={r.receipt} target="_blank" rel="noopener noreferrer"><i aria-hidden="true" className="fa-solid fa-link" />View receipt</a>}
            </span>
            <span className="debt-schedule-amount finance-detail-money">{peso2(r.amount)}{receivable && r.status === "partial" && <small className="finance-schedule-paid-date">remaining</small>}</span>
            <span className="debt-schedule-status"><Badge tone={r.status === "paid" ? "success" : isNext ? "accent" : "neutral"}>{r.status === "paid" ? receivable ? "Received" : "Paid" : receivable && r.status === "partial" ? "Partly received" : isNext ? "Next up" : "Upcoming"}</Badge></span>
          </div>;
        })}</div> : isPublic && !schedErr ? <p className="detail-copy" role="status">Loading schedule…</p> : isPublic && schedErr ? <p className="detail-copy">Couldn't load the schedule right now. It's on the <a href={shareUrl} target="_blank" rel="noopener noreferrer">public page</a>.</p> : <p className="detail-copy">{receivable ? "No collection schedule set." : "The full schedule lives in this loan's vault note."}</p>}
      </Card>
      </DebtWorkspace>
    </Page>
  );
}
